import React, { useRef } from "react";
import styled from "styled-components";
import {
  FaBold,
  FaItalic,
  FaHeading,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaCode,
  FaLink,
  FaImage,
  FaTasks,
  FaStrikethrough,
} from "react-icons/fa";
import { BiCodeBlock } from "react-icons/bi";

const EditorContainer = styled.div`
  border: 2px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-white);
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--color-bg);
  border-bottom: 2px solid var(--color-border);
  flex-wrap: wrap;
  position: relative;
  overflow: visible;
  border-radius: 6px 6px 0 0;
`;

const ToolbarSection = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0 0.25rem;

  &:not(:last-child) {
    border-right: 1px solid var(--color-border);
  }
`;

const ToolButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--color-text);
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: var(--color-white);
    color: var(--color-accent);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  margin-bottom: 0.25rem;
  z-index: 1000;

  /* Add a small arrow pointing down */
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid rgba(0, 0, 0, 0.8);
  }

  ${ToolButton}:hover & {
    opacity: 1;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: none;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  min-height: ${(props) => props.minHeight || "400px"};
  resize: vertical;
  border-radius: 0 0 6px 6px;

  &:focus {
    outline: none;
  }
`;

const HeadingMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.25rem;
  margin-top: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: ${(props) => (props.show ? "block" : "none")};
`;

const HeadingOption = styled.button`
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: var(--color-bg);
  }
`;

const MarkdownEditor = ({
  value,
  onChange,
  minHeight,
  placeholder,
  disabled,
}) => {
  const textareaRef = useRef(null);
  const headingMenuRef = useRef(null);
  const [showHeadingMenu, setShowHeadingMenu] = React.useState(false);

  // Close heading menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        headingMenuRef.current &&
        !headingMenuRef.current.contains(event.target) &&
        !event.target.closest("[data-heading-button]")
      ) {
        setShowHeadingMenu(false);
      }
    };

    if (showHeadingMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHeadingMenu]);

  const insertFormatting = (before, after = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText =
      value.substring(0, start) +
      before +
      selectedText +
      after +
      value.substring(end);

    onChange({ target: { value: newText, name: textarea.name } });

    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(start + before.length, end + before.length);
      } else {
        textarea.setSelectionRange(
          start + before.length,
          start + before.length
        );
      }
    }, 0);
  };

  const insertAtLineStart = (prefix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== "\n") {
      lineStart--;
    }

    // Insert the prefix at the start of the line
    const newText =
      value.substring(0, lineStart) + prefix + value.substring(lineStart);
    onChange({ target: { value: newText, name: textarea.name } });

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const insertLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || "link text";

    insertFormatting(`[${selectedText}](`, ")");
  };

  const insertImage = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || "alt text";

    insertFormatting(`![${selectedText}](`, ")");
  };

  const insertChecklist = () => {
    insertAtLineStart("- [ ] ");
  };

  const handleHeadingClick = (level) => {
    const prefix = "#".repeat(level) + " ";
    insertAtLineStart(prefix);
    setShowHeadingMenu(false);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case "b":
          e.preventDefault();
          insertFormatting("**", "**");
          break;
        case "i":
          e.preventDefault();
          insertFormatting("*", "*");
          break;
        case "k":
          e.preventDefault();
          insertLink();
          break;
        case "q":
          e.preventDefault();
          insertAtLineStart("> ");
          break;
        case "e":
          e.preventDefault();
          insertFormatting("`", "`");
          break;
        case "h":
          e.preventDefault();
          insertAtLineStart("# ");
          break;
        default:
          break;
      }
    }
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarSection>
          <ToolButton
            onClick={() => insertFormatting("**", "**")}
            disabled={disabled}
          >
            <FaBold />
            <Tooltip>Bold (Ctrl+B)</Tooltip>
          </ToolButton>
          <ToolButton
            onClick={() => insertFormatting("*", "*")}
            disabled={disabled}
          >
            <FaItalic />
            <Tooltip>Italic (Ctrl+I)</Tooltip>
          </ToolButton>
          <ToolButton
            onClick={() => insertFormatting("~~", "~~")}
            disabled={disabled}
          >
            <FaStrikethrough />
            <Tooltip>Strikethrough</Tooltip>
          </ToolButton>
        </ToolbarSection>

        <ToolbarSection>
          <div style={{ position: "relative" }}>
            <ToolButton
              onClick={() => setShowHeadingMenu(!showHeadingMenu)}
              disabled={disabled}
              data-heading-button
            >
              <FaHeading />
              <Tooltip>Heading (Ctrl+H)</Tooltip>
            </ToolButton>
            <HeadingMenu show={showHeadingMenu} ref={headingMenuRef}>
              <HeadingOption onClick={() => handleHeadingClick(1)}>
                <h1 style={{ margin: 0, fontSize: "1.2rem" }}>Heading 1</h1>
              </HeadingOption>
              <HeadingOption onClick={() => handleHeadingClick(2)}>
                <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Heading 2</h2>
              </HeadingOption>
              <HeadingOption onClick={() => handleHeadingClick(3)}>
                <h3 style={{ margin: 0, fontSize: "1rem" }}>Heading 3</h3>
              </HeadingOption>
            </HeadingMenu>
          </div>
        </ToolbarSection>

        <ToolbarSection>
          <ToolButton
            onClick={() => insertAtLineStart("- ")}
            disabled={disabled}
          >
            <FaListUl />
            <Tooltip>Unordered List</Tooltip>
          </ToolButton>
          <ToolButton
            onClick={() => insertAtLineStart("1. ")}
            disabled={disabled}
          >
            <FaListOl />
            <Tooltip>Ordered List</Tooltip>
          </ToolButton>
          <ToolButton onClick={insertChecklist} disabled={disabled}>
            <FaTasks />
            <Tooltip>Checklist</Tooltip>
          </ToolButton>
        </ToolbarSection>

        <ToolbarSection>
          <ToolButton
            onClick={() => insertAtLineStart("> ")}
            disabled={disabled}
          >
            <FaQuoteLeft />
            <Tooltip>Quote (Ctrl+Q)</Tooltip>
          </ToolButton>
        </ToolbarSection>

        <ToolbarSection>
          <ToolButton
            onClick={() => insertFormatting("`", "`")}
            disabled={disabled}
          >
            <FaCode />
            <Tooltip>Inline Code (Ctrl+E)</Tooltip>
          </ToolButton>
          <ToolButton
            onClick={() => insertFormatting("```\n", "\n```")}
            disabled={disabled}
          >
            <BiCodeBlock />
            <Tooltip>Code Block</Tooltip>
          </ToolButton>
        </ToolbarSection>

        <ToolbarSection>
          <ToolButton onClick={insertLink} disabled={disabled}>
            <FaLink />
            <Tooltip>Link (Ctrl+K)</Tooltip>
          </ToolButton>
          <ToolButton onClick={insertImage} disabled={disabled}>
            <FaImage />
            <Tooltip>Image</Tooltip>
          </ToolButton>
        </ToolbarSection>
      </Toolbar>

      <TextArea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        minHeight={minHeight}
        placeholder={placeholder}
        disabled={disabled}
        name="content"
      />
    </EditorContainer>
  );
};

export default MarkdownEditor;
